import { validatedDisplayView } from "../i18n/display-view.js";
import { portalText, portalLocale } from "../i18n/portal.js";
/**
 * What the Review-and-optimise surface renders (P14-07).
 *
 * The depth chooser is the load-bearing part. Standard reads transcripts; **Ultra reads the
 * audio** — pauses, interruptions, tone, mispronounced prices — which the shared text-only
 * GPU pool cannot serve at all, so Ultra runs on the frontier provider behind the provider
 * wall. Two consequences show up in these types and nowhere else:
 *
 *   {@link OptimisationReviewDepthOptionView.unavailableReason} is a named value rather than
 *   a disabled flag, because the reason a workspace cannot buy an Ultra review is usually
 *   "your recordings are already deleted" — a consequence of their own retention policy,
 *   which they can change — and a greyed-out button that does not say so is a dead end.
 *
 *   {@link OptimisationReviewView.recordingAvailableCount} is shown beside the conversation
 *   count, so "1,284 conversations, 96 recordings left" is visible before €18.40 is spent on
 *   a review of 96 calls.
 *
 * Findings are accepted or rejected **one at a time** and nothing is accepted by default:
 * `acceptedCount` starts at zero, so publishing is a decision rather than an omission.
 *
 * AMENDED 2026-10-28 (plan-032 WAPP-OR, design §43.9, §43.11; owner AP-0 "build with defaults"). The factory now runs a
 * started review, so these types carry what the engine records: why a review stopped (`failureReason`, every reason
 * ends "Nothing was charged."), how many conversations it actually read (`conversationReadCount`, "Read N of M"), and
 * per finding its `category`, `fixKind` (`answer` | `config_patch` | `advice` — advice is changed by hand, never
 * accepted), `position`, `adviceTarget` (where "Change it yourself" points) and `evidenceLine` — the conversation lines
 * the finding rests on, read live from the transcript: `shown` to a reader who may read transcripts, `withheld`
 * otherwise, `gone` once retention or erasure left no words (OD-4). The quote's two new reasons: `not_available_yet`
 * (the api switch is off for people, or Ultra, which is not offered in the first release — OD-1, OD-6) and
 * `not_offered` (an agent template that is never reviewed). The list (`OptimisationReviewSummaryView`) restores the
 * newest review after a reload. Ultra still "listens to the recordings" in its card's words: the card says what it
 * will do, and is marked "Coming later" until it can.
 */
import type { Money } from "@wetalk/domain";

import { formatMoney } from "../money.js";

export type OptimisationReviewDepthView = "standard" | "ultra";

export type OptimisationReviewScopeView = "last_24h" | "last_7d" | "since_last_version";

export type OptimisationReviewStateView = "idle" | "running" | "done" | "failed";

export type OptimisationReviewPayerView = "customer" | "wetalk";

export type OptimisationFindingDecisionView = "pending" | "accepted" | "rejected";

export type OptimisationReviewUnavailableReasonView =
  | "not_available_yet"
  | "not_offered"
  | "no_conversations_in_scope"
  | "recordings_expired"
  | "recording_consent_refused"
  | "insufficient_credit";

/** Why a review stopped (design §43.7.2). Every one means nothing was charged. */
export type OptimisationReviewFailureReasonView =
  | "insufficient_credit"
  | "no_readable_conversations"
  | "model_refused"
  | "analysis_invalid"
  | "took_too_long"
  | "not_offered"
  | "cancelled_by_operator";

/** What a finding's fix is (design §43.6.1): applied to a draft (`answer`, `config_patch`) or changed by hand. */
export type OptimisationFixKindView = "answer" | "config_patch" | "advice";

/** Where an `advice` finding's change is made (design §43.11's links). */
export type OptimisationAdviceTargetView =
  | "menu"
  | "questionnaire"
  | "documents"
  | "website"
  | "voice"
  | "transfer_number"
  | "other";

/** One conversation line a finding rests on, read live from the transcript. */
export interface OptimisationFindingEvidenceLineItemView {
  readonly conversationId: string;
  readonly turnIndex: number;
  readonly speaker: "caller" | "assistant";
  readonly text: string;
  readonly startedText: string;
}

/**
 * The lines under "Show the conversations": `shown` (with the lines that still have words), `withheld` (this reader
 * may not read transcripts) or `gone` (the conversations' words are no longer kept). Never stored by the review.
 */
export type OptimisationFindingEvidenceLineView =
  | { readonly state: "shown"; readonly line: readonly OptimisationFindingEvidenceLineItemView[] }
  | { readonly state: "withheld" }
  | { readonly state: "gone" };

/** One card in the depth chooser. */
export interface OptimisationReviewDepthOptionView {
  readonly depth: OptimisationReviewDepthView;
  readonly name: string;
  readonly dek: string;
  readonly point: readonly string[];
  readonly price: Money;
  /** `null` when this depth can run as asked. */
  readonly unavailableReason: OptimisationReviewUnavailableReasonView | null;
}

export interface OptimisationReviewQuoteView {
  readonly voiceAgentId: string;
  readonly scope: OptimisationReviewScopeView;
  readonly conversationCount: number;
  /** How many of those still have audio under the workspace's retention policy. */
  readonly recordingAvailableCount: number;
  readonly option: readonly OptimisationReviewDepthOptionView[];
}

export interface OptimisationFindingView {
  readonly optimisationFindingId: string;
  readonly issue: string;
  readonly evidence: string;
  readonly affectedConversationCount: number;
  /** "14 calls", "6 callbacks" — the unit varies, so the server composes it. */
  readonly affectedText: string;
  readonly proposedFixText: string;
  readonly decision: OptimisationFindingDecisionView;
  readonly iconName: string;
  /** The kind of problem, from the agent template's own list; `null` on a finding older than the engine. */
  readonly category: string | null;
  /** `null` on a finding older than the engine (decided as before: accepted or rejected). */
  readonly fixKind: OptimisationFixKindView | null;
  /** Its place in the review, most conversations first; `null` on a finding older than the engine. */
  readonly position: number | null;
  /** For `advice`, where the change is made; `null` for the other kinds. */
  readonly adviceTarget: OptimisationAdviceTargetView | null;
  readonly evidenceLine: OptimisationFindingEvidenceLineView;
}

/**
 * Who paid, and why WeTalk did when WeTalk did.
 *
 * A discriminated union for the same reason the domain's is: an absorbed review with no
 * recorded reason is not a state this type can hold, so a screen cannot render one and an
 * operator cannot create one.
 */
export type OptimisationReviewBillingView =
  | { readonly payer: "customer"; readonly amount: Money }
  | { readonly payer: "wetalk"; readonly amount: Money; readonly absorbedReason: string };

export interface OptimisationReviewView {
  readonly optimisationReviewId: string;
  readonly voiceAgentId: string;
  readonly depth: OptimisationReviewDepthView;
  readonly scope: OptimisationReviewScopeView;
  readonly state: OptimisationReviewStateView;
  readonly conversationCount: number;
  readonly recordingAvailableCount: number;
  readonly billing: OptimisationReviewBillingView;
  readonly finding: readonly OptimisationFindingView[];
  readonly acceptedCount: number;
  /** "v8" — what publishing would create. */
  readonly nextAgentVersionLabel: string;
  readonly startedText: string | null;
  readonly finishedText: string | null;
  /** Set once the accepted fixes have been published. */
  readonly publishedAgentVersionId: string | null;
  /** Set exactly when `failed` (design §43.7.2); `null` on a failed review older than the engine. */
  readonly failureReason: OptimisationReviewFailureReasonView | null;
  /** How many of the window's conversations the review read; set once it is `done`. */
  readonly conversationReadCount: number | null;
}

/** One review in the VoiceAgent's list (`GET …/optimisation-reviews`), newest first; its findings are on the detail. */
export interface OptimisationReviewSummaryView {
  readonly optimisationReviewId: string;
  readonly depth: OptimisationReviewDepthView;
  readonly scope: OptimisationReviewScopeView;
  readonly state: OptimisationReviewStateView;
  readonly failureReason: OptimisationReviewFailureReasonView | null;
  readonly findingCount: number;
  readonly requestedText: string;
  readonly finishedText: string | null;
  readonly publishedAgentVersionId: string | null;
}

/** One page of the list; `nextCursor` is `null` on the last page. */
export interface OptimisationReviewPageView {
  readonly row: readonly OptimisationReviewSummaryView[];
  readonly nextCursor: string | null;
}

export interface StartOptimisationReviewRequestView {
  readonly voiceAgentId: string;
  readonly depth: OptimisationReviewDepthView;
  readonly scope: OptimisationReviewScopeView;
  readonly payer: OptimisationReviewPayerView;
  /** Required when `payer` is `wetalk`; the gateway refuses a blank one. */
  readonly absorbedReason: string | null;
}

export interface PublishedOptimisationReviewView {
  readonly optimisationReviewId: string;
  readonly agentVersionId: string;
  readonly liveVersionSeq: number;
  readonly appliedFindingCount: number;
}

// ---------------------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------------------

/** The two depth cards, in the design's own words. Prices come from the API, not from here. */
export const DEPTH_COPY: readonly {
  readonly depth: OptimisationReviewDepthView;
  readonly name: string;
  readonly dek: string;
  readonly point: readonly string[];
}[] = [
  validatedDisplayView({
    depth: "standard",
    name: "Standard review",
    get dek() { return portalText("shell", "reads_the_transcripts_of_recent_calls_and_drafts_fixes_for_e3f953c5"); },
    get point() { return [
      portalText("shell", "unanswered_questions_and_wrong_answers_5169b143"),
      portalText("shell", "wording_that_confused_callers_0f35c2b4"),
      portalText("shell", "usually_finishes_in_a_few_minutes_53f9eadd"),
    ]; },
  }),
  validatedDisplayView({
    depth: "ultra",
    name: "Ultra review",
    get dek() { return portalText("shell", "listens_to_the_recordings_as_well_so_it_hears_what_a_trans_65d46817"); },
    get point() { return [
      portalText("shell", "pauses_interruptions_and_people_talking_over_the_agent_73850cfe"),
      portalText("shell", "tone_pace_and_mispronounced_names_or_prices_9029fe08"),
      portalText("shell", "where_callers_sounded_confused_before_they_said_so_f359e588"),
    ]; },
  }),
];

const UNAVAILABLE_COPY = () => (new Map<string, string>([
  // plan-032 (OD-6, OD-1): the api switch is off for people, or the depth is Ultra, which is not offered yet.
  ["not_available_yet", portalText("operations", "review.unavailable.not_available_yet")],
  ["not_offered", portalText("operations", "review.unavailable.not_offered")],
  ["no_conversations_in_scope", portalText("shell", "there_are_no_calls_in_this_window_yet_3987775f")],
  [
    "recordings_expired",
    portalText("shell", "the_recordings_for_this_window_have_already_been_deleted_u_d44e7114"),
  ],
  [
    "recording_consent_refused",
    portalText("shell", "the_callers_in_this_window_did_not_consent_to_being_record_0f8f67b5"),
  ],
  // Customer-portal review #119: a trial account with free minutes left was told it had no credit.
  // Reviews are charged to credit, never to the free call minutes, and the sentence says so.
  [
    "insufficient_credit",
    portalText("shell", "reviews_are_paid_from_the_account_s_credit_not_from_free_c_9755006a"),
  ],
]));

export function unavailableReasonText(reason: string): string {
  return UNAVAILABLE_COPY().get(reason) ?? reason;
}

/** The balance figures the credit notice reads — the Billing screen's live balance. */
export interface OptimisationReviewCreditView {
  readonly available: Money;
  readonly trialSecondsRemaining: number;
}

/**
 * The ONE credit notice under the paid review (customer-portal review #95: it was printed three
 * times; #119: a trial account with free minutes was told it had "not enough credit" without
 * being told why). It says what is true for this account: reviews are charged to credit, free
 * minutes are for calls only, what the credit is and what the review costs. `credit` is
 * `undefined` when the balance could not be read (a role without billing): the sentence is still
 * true, only shorter.
 */
export function optimisationReviewCreditNoticeOf(price: Money, credit: OptimisationReviewCreditView | undefined): string {
  const lead = portalText("shell", "reviews_are_paid_from_the_account_s_credit_not_from_free_c_476984dc");
  if (credit === undefined) {
    return portalText("shell", "value_the_credit_on_the_account_does_not_cover_this_review_6c812d30", { v0: String(lead), v1: String(formatMoney(price)) });
  }
  const freeMinute = Math.floor(credit.trialSecondsRemaining / 60);
  const trial =
    freeMinute > 0
      ? portalText("shell", "your_value_still_there_for_calls_7f0e7f86", { v0: String(freeMinute === 1 ? "free minute is" : `${freeMinute.toLocaleString((portalLocale() === "el" ? "el-GR" : "en-GB"))} free minutes are`) })
      : "";
  return (
    portalText("shell", "value_this_review_costs_value_and_the_account_has_value_of_05920bec", { v0: String(lead), v1: String(formatMoney(price)), v2: String(formatMoney(credit.available)), v3: String(trial) })
  );
}

/**
 * Whether a depth card repeats its reason. Reasons about the whole window or the whole account
 * (no calls, not enough credit) are said once under the cards; only a reason that is this depth's
 * own (Ultra with no audio) is said on its card.
 */
export function isDepthOwnReason(reason: string): boolean {
  return reason === "recordings_expired" || reason === "recording_consent_refused";
}

/**
 * plan-032 (OD-6): call reviews are not open to this person yet — the api marks EVERY option `not_available_yet` while
 * its switch is off. The panel then shows the button disabled and "Coming soon". Ultra alone `not_available_yet` is the
 * card's own "Coming later" (OD-1), never this.
 */
export function isOptimisationReviewComingSoon(quote: Pick<OptimisationReviewQuoteView, "option">): boolean {
  return quote.option.length > 0 && quote.option.every((one) => one.unavailableReason === "not_available_yet");
}

/** plan-032: this VoiceAgent's agent template is never reviewed (`help`): nothing is offered. */
export function isOptimisationReviewNotOffered(quote: Pick<OptimisationReviewQuoteView, "option">): boolean {
  return quote.option.length > 0 && quote.option.every((one) => one.unavailableReason === "not_offered");
}

const FAILURE_COPY = () => (new Map<OptimisationReviewFailureReasonView, string>([
  ["insufficient_credit", portalText("operations", "review.failure.insufficient_credit")],
  ["no_readable_conversations", portalText("operations", "review.failure.no_readable_conversations")],
  ["model_refused", portalText("operations", "review.failure.model_refused")],
  ["analysis_invalid", portalText("operations", "review.failure.analysis_invalid")],
  ["took_too_long", portalText("operations", "review.failure.took_too_long")],
  ["not_offered", portalText("operations", "review.failure.not_offered")],
  ["cancelled_by_operator", portalText("operations", "review.failure.cancelled_by_operator")],
]));

/**
 * Why a review stopped, in the customer's words, always followed by "Nothing was charged." (design §43.7.2). A failed
 * review older than the engine carries no reason: it gets the general sentence.
 */
export function optimisationFailureText(reason: OptimisationReviewFailureReasonView | null): string {
  const lead = reason === null ? undefined : FAILURE_COPY().get(reason);
  return portalText("operations", "review.failure.nothing_charged", {
    reason: lead === undefined ? portalText("operations", "review.failure.unknown") : lead,
  });
}

/** The screen "Change it yourself" opens for an advice finding (design §43.11); `null` names none. */
export type OptimisationAdviceScreenView = "edit" | "questions" | "channelBinding" | null;

export function optimisationAdviceScreenOf(target: OptimisationAdviceTargetView | null): OptimisationAdviceScreenView {
  switch (target) {
    case "menu":
    case "documents":
    case "website":
    case "voice":
      return "edit";
    case "questionnaire":
      return "questions";
    case "transfer_number":
      return "channelBinding";
    case "other":
    case null:
      return null;
  }
}

const SCOPE_COPY = () => (new Map<string, string>([
  ["last_24h", portalText("shell", "the_last_day_7aab1dca")],
  ["last_7d", portalText("shell", "the_last_seven_days_5043860d")],
  ["since_last_version", portalText("shell", "since_the_current_version_was_published_293253c2")],
]));

export function optimisationScopeLabel(scope: string): string {
  return SCOPE_COPY().get(scope) ?? scope;
}

/**
 * What the header says a review read (design §43.11, "Read N of M"): the conversations it actually read of the
 * window's count once it is done; a review older than the engine says only how many were in the window.
 * AMENDED 2026-10-28 (plan-032): no longer worded by depth — only Standard runs (OD-1).
 */
export function reviewedCountText(conversationReadCount: number | null, conversationCount: number): string {
  const locale = portalLocale() === "el" ? "el-GR" : "en-GB";
  const figure = conversationCount.toLocaleString(locale);
  if (conversationReadCount === null) {
    return portalText("operations", "review.conversations_in_window", { count: conversationCount, figure });
  }
  return portalText("operations", "review.read_of", {
    count: conversationCount,
    read: conversationReadCount.toLocaleString(locale),
    figure,
  });
}

/**
 * plan-032 (design §43.11): "N of M conversations" under a finding — the conversations it affected of those the review
 * read; a finding of a review older than the engine keeps its own count ("14 calls").
 */
export function affectedOfReadText(affectedConversationCount: number, conversationReadCount: number): string {
  const locale = portalLocale() === "el" ? "el-GR" : "en-GB";
  return portalText("shell", "reviews.affected_of", {
    count: conversationReadCount,
    affected: affectedConversationCount.toLocaleString(locale),
    figure: conversationReadCount.toLocaleString(locale),
  });
}

/**
 * The minimum length of a recorded reason for absorbing a review's cost.
 *
 * The same twelve characters `packages/domain` enforces. Restated here so the dialog can
 * disable its own button rather than discovering the refusal after a round trip; the domain
 * remains the thing that actually decides.
 */
export const ABSORBED_REASON_MIN_LENGTH = 12;

export function isUsableAbsorbedReason(raw: string): boolean {
  return raw.trim().length >= ABSORBED_REASON_MIN_LENGTH;
}
